import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { LayoutDashboard, Inbox, ShieldCheck, LogOut, Building2, Users, MessagesSquare, Video, Bell, AlertTriangle, FileText, X, Cpu, MapPin, Megaphone, Landmark } from "lucide-react";
import { NotificationPrefs } from "./NotificationPrefs";
import TimeClockBar from "./TimeClockBar";

function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ count: 0, review_count: 0, items: [] });
  useEffect(() => {
    let stop = false;
    const poll = async () => {
      try { const { data } = await api.get("/notifications"); if (!stop) setData(data); } catch { /* transient */ }
    };
    poll();
    const t = setInterval(poll, 15000);
    return () => { stop = true; clearInterval(t); };
  }, []);
  const rel = (iso) => {
    if (!iso) return "";
    const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
    if (m < 1) return "just now"; if (m < 60) return `${m}m`; const h = Math.floor(m / 60);
    return h < 24 ? `${h}h` : `${Math.floor(h / 24)}d`;
  };
  const icon = (t) => t === "review" ? <AlertTriangle className="h-3.5 w-3.5 text-rose-500" /> : t === "new_order" ? <Bell className="h-3.5 w-3.5 text-[#1A6FBF]" /> : <FileText className="h-3.5 w-3.5 text-teal" />;
  return (
    <div className="relative">
      <button data-testid="notif-bell" onClick={() => setOpen((v) => !v)}
        className="relative flex items-center justify-center rounded-md border border-white/15 h-8 w-8 text-slate-300 hover:bg-white/5 transition-colors">
        <Bell className="h-4 w-4" />
        {data.count > 0 && <span data-testid="notif-badge" className="absolute -right-1 -top-1 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold h-4 min-w-[16px] px-1">{data.count}</span>}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div data-testid="notif-panel" className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-auto rounded-lg border border-slate-200 bg-white shadow-xl z-50">
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 sticky top-0 bg-white">
              <span className="text-sm font-bold text-[#1A395E]">Notifications</span>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
            </div>
            {data.items.length === 0 && <div className="px-4 py-8 text-center text-xs text-slate-400">You're all caught up.</div>}
            {data.items.map((n) => (
              <button key={n.id} data-testid={`notif-item-${n.type}`} onClick={() => { setOpen(false); navigate(`/orders/${n.order_id}`); }}
                className="w-full text-left flex items-start gap-2 px-3 py-2.5 border-b border-slate-50 hover:bg-blue-50/60 transition-colors">
                <span className="mt-0.5">{icon(n.type)}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-slate-800 truncate">{n.title}</span>
                    <span className="text-[10px] text-slate-400 shrink-0">{rel(n.at)}</span>
                  </div>
                  <div className="text-[11px] font-mono text-[#1A6FBF]">{n.order_ref}</div>
                  {n.body && <div className="text-[11px] text-slate-500 truncate">{n.body}</div>}
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [dmUnread, setDmUnread] = useState(0);
  const prevRef = useRef(0);

  useEffect(() => {
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get("/chat/unread");
        if (stop) return;
        if (data.total > prevRef.current) toast.message("💬 New clarification message");
        prevRef.current = data.total;
      } catch (e) { /* transient */ }
      try { const { data } = await api.get("/dm/meta/unread"); if (!stop) setDmUnread(data.total || 0); } catch (e) { /* transient */ }
    };
    poll();
    const t = setInterval(poll, 5000);
    return () => { stop = true; clearInterval(t); };
  }, []);

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const items = isAdmin
    ? [
        { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testid: "nav-admin", end: true },
        { to: "/intake", label: "Email Intake", icon: Inbox, testid: "nav-intake" },
        { to: "/admin/users", label: "Team", icon: Users, testid: "nav-users" },
        { to: "/admin/ai-spend", label: "AI Spend", icon: Cpu, testid: "nav-ai-spend" },
        { to: "/admin/county-portals", label: "County Portals", icon: MapPin, testid: "nav-county-portals" },
        { to: "/admin/search-instructions", label: "Search Instructions", icon: FileText, testid: "nav-search-instructions" },
        { to: "/admin/court-links", label: "Court Links", icon: Landmark, testid: "nav-court-links" },
        { to: "/admin/marketing", label: "Marketing Hub", icon: Megaphone, testid: "nav-marketing" },
        { to: "/messages", label: "Communication Hub", icon: MessagesSquare, testid: "nav-comm-hub" },
        { to: "/meet", label: "Team Room", icon: Video, testid: "nav-meet" },
      ]
    : [
        { to: "/dashboard", label: "My Queue", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
        { to: "/messages", label: "Communication Hub", icon: MessagesSquare, testid: "nav-comm-hub" },
        { to: "/meet", label: "Team Room", icon: Video, testid: "nav-meet" },
      ];

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="sticky top-0 z-30 bg-navy border-b border-navy-2 shadow-sm">
        <div className="flex items-center gap-4 px-4 lg:px-6 h-14">
          {/* Brand */}
          <div className="flex items-center gap-2 shrink-0">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-teal text-teal-foreground">
              <Building2 className="h-5 w-5" />
            </div>
            <div className="leading-tight hidden sm:block">
              <div className="font-display font-700 text-[14px] tracking-tight text-white">TitleHub</div>
              <div className="text-[9px] uppercase tracking-widest text-teal">Operations</div>
            </div>
          </div>

          {/* Top nav */}
          <nav className="flex items-center gap-1 flex-1 overflow-x-auto no-scrollbar">
            {items.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `relative flex items-center gap-2 rounded-md px-3 py-1.5 text-[13px] font-medium whitespace-nowrap transition-colors duration-200 ${
                    isActive ? "bg-teal text-teal-foreground shadow-sm" : "text-slate-300 hover:bg-white/10 hover:text-white"
                  }`
                }
              >
                <n.icon className="h-4 w-4" />
                <span>{n.label}</span>
                {n.testid === "nav-comm-hub" && dmUnread > 0 && (
                  <span data-testid="dm-unread-badge" className="inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold h-4 min-w-[16px] px-1">{dmUnread}</span>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Right: role, user, prefs, logout */}
          <div className="flex items-center gap-2 shrink-0">
            {isAdmin ? (
              <span className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-teal/40 bg-accent px-2.5 py-1 text-[11px] font-semibold text-accent-foreground">
                <ShieldCheck className="h-3.5 w-3.5" /> Admin
              </span>
            ) : (
              <span className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-slate-300">
                <Users className="h-3.5 w-3.5" /> Employee
              </span>
            )}
            <NotificationBell />
            <NotificationPrefs isAdmin={isAdmin} />
            <div className="text-right leading-tight hidden lg:block">
              <div className="text-[13px] font-semibold text-slate-100">{user?.name}</div>
              <div className="text-[11px] text-slate-400 font-mono">{user?.email}</div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="flex items-center gap-1.5 rounded-md border border-white/15 px-2.5 py-1.5 text-[13px] text-slate-300 hover:bg-white/5 transition-colors duration-200"
            >
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <TimeClockBar />

      <main className="flex-1 w-full p-6">{children}</main>
    </div>
  );
}
